import ExcelJS from 'exceljs'
import type { TenantImportRent, TenantImportRow } from '@einwert/valuation-api-client'
import { MonetaryValue, tenantRentalStatusOptions, tenantUsageTypeOptions } from 'ComponentLibrary'

const LEASE_LABELS = { fixed: 'Frei', indexed: 'Index', graduated: 'Staffel' }
const MAX_GRADUATIONS = 4
const MAX_PERIODS = 3

function absoluteRent(rent: TenantImportRent, row: TenantImportRow): MonetaryValue {
    const factor = { absolute: 1, per_area: row.area_sqm ?? 0, per_unit: row.number_of_units ?? 0 }[rent.payment_scope]
    return MonetaryValue.fromDecimal(rent.amount).multiply(factor)
}

function datedVacancies(row: TenantImportRow) {
    return row.vacancy_periods.filter((period) => period.start_date && period.end_date).slice(0, MAX_PERIODS)
}

/** Facts that the template cannot carry in structured columns, retained in Bemerkung. */
export function tenantImportExportLimitations(row: TenantImportRow): string[] {
    const vacancies = datedVacancies(row)
    return [
        ...row.graduations
            .slice(MAX_GRADUATIONS)
            .map(
                ({ date, rent }) =>
                    `Staffel ab ${date}: ${rent.amount} EUR ${rent.payment_scope === 'per_area' ? '/m²' : rent.payment_scope === 'per_unit' ? '/Einheit' : 'absolut'} ${rent.payment_period === 'yearly' ? 'jährlich' : 'monatlich'}`
            ),
        ...row.rent_free_periods.slice(MAX_PERIODS).map((period) => `Mietfrei: ${period.start_date} – ${period.end_date}`),
        ...row.vacancy_periods
            .filter((period) => !vacancies.includes(period))
            .map(
                (period) =>
                    `Leerstand: ${period.start_date ? `ab ${period.start_date}` : 'Beginn nicht angegeben'}${period.end_date ? ` bis ${period.end_date}` : `, ${period.duration} Monate`}`
            ),
    ]
}

/** Fill the einwert valuation template; all amounts in the workbook are EUR. */
export async function createTenantImportWorkbook(template: ArrayBuffer, rows: TenantImportRow[]): Promise<ArrayBuffer> {
    if (!rows.length || rows.some((row) => !row.prepared || !['rented', 'unrented', 'self_used'].includes(row.rental_status ?? ''))) {
        throw new Error('The tenant list is not ready for Excel export.')
    }
    const workbook = new ExcelJS.Workbook()
    await workbook.xlsx.load(template)
    workbook.calcProperties.fullCalcOnLoad = true
    const sheet = workbook.getWorksheet('ML Template')
    if (!sheet) {
        throw new Error('The tenant template is missing its ML Template sheet.')
    }
    const rowStyles = Array.from({ length: 42 }, (_, index) => sheet.getRow(5).getCell(index + 1).style)
    sheet.eachRow((row, rowNumber) => {
        if (rowNumber >= 5) {
            row.eachCell((cell) => {
                cell.value = null
            })
        }
    })
    rows.forEach((row, index) => {
        const line = sheet.getRow(index + 5)
        rowStyles.forEach((style, column) => {
            line.getCell(column + 1).style = { ...style }
        })
        const set = (column: string | number, value: string | number | Date | null | undefined) => {
            if (value === null || value === undefined || value === '') {
                return
            }
            const cell = line.getCell(column)
            cell.value = value
            if (value instanceof Date) {
                cell.numFmt = 'dd.mm.yyyy'
            } else if (typeof value === 'number') {
                cell.numFmt = '#,##0.00'
            }
        }
        const setDate = (column: string | number, value: string | null | undefined) => {
            if (value) {
                set(column, new Date(`${value}T00:00:00Z`))
            }
        }
        const setRent = (columns: [string, string, string, string], rent: TenantImportRent | null | undefined) => {
            const [monthly, perArea, perUnit, annual] = columns
            const yearly = rent?.payment_period === 'yearly'
            const paymentScope = rent?.payment_scope ?? 'absolute'
            const source = yearly ? annual : { absolute: monthly, per_area: perArea, per_unit: perUnit }[paymentScope]
            // Keep the detected basis editable; absent rents use a blank monthly input with calculated variants.
            const amount = rent
                ? yearly
                    ? absoluteRent(rent, row).toDecimal()
                    : MonetaryValue.fromDecimal(rent.amount).toDecimal()
                : undefined
            set(source, amount)
            const factor = { absolute: 1, per_area: row.area_sqm, per_unit: row.number_of_units }[paymentScope]
            const monthlyAmount = amount === undefined ? '' : yearly ? amount / 12 : factor ? amount * factor : ''
            const address = (column: string) => `${column}${line.number}`
            const setFormula = (column: string, formula: string, result: number | '') => {
                if (column !== source) {
                    const cell = line.getCell(column)
                    cell.value = { formula, result }
                    cell.numFmt = '#,##0.00'
                }
            }
            if (yearly) {
                setFormula(monthly, `IF(${address(annual)}="","",${address(annual)}/12)`, monthlyAmount)
            } else if (paymentScope !== 'absolute') {
                const quantity = address(paymentScope === 'per_area' ? 'F' : 'G')
                setFormula(monthly, `IF(OR(${address(source)}="",${quantity}=0),"",${address(source)}*${quantity})`, monthlyAmount)
            }
            setFormula(
                perArea,
                `IF(OR(${address(monthly)}="",${address('F')}=0),"",${address(monthly)}/${address('F')})`,
                monthlyAmount !== '' && row.area_sqm ? monthlyAmount / row.area_sqm : ''
            )
            setFormula(
                perUnit,
                `IF(OR(${address(monthly)}="",${address('G')}=0),"",${address(monthly)}/${address('G')})`,
                monthlyAmount !== '' && row.number_of_units ? monthlyAmount / row.number_of_units : ''
            )
            setFormula(annual, `IF(${address(monthly)}="","",${address(monthly)}*12)`, monthlyAmount === '' ? '' : monthlyAmount * 12)
        }
        if (!row.usage_type) {
            throw new Error('A tenant usage is required.')
        }
        set('A', row.reference_number)
        set('B', row.floor)
        set('C', tenantUsageTypeOptions.find((option) => option.value === row.usage_type)?.label)
        set('D', row.sub_usage_type)
        set('E', tenantRentalStatusOptions.find((option) => option.value === row.rental_status)?.label)
        set('F', row.area_sqm)
        set('G', row.number_of_units)
        if (row.rental_status !== 'unrented' && row.contract_rent) {
            setRent(['H', 'I', 'J', 'K'], row.contract_rent)
        }
        set('L', row.tenant_name)
        set('M', row.lease_number)
        set('N', row.options && `${row.options.frequency}x${row.options.years}`)
        setDate('O', row.start_date)
        setDate('P', row.end_date)
        if (row.lease_type) {
            if (row.lease_type === 'unknown') {
                throw new Error('The Excel template cannot represent an unknown lease type.')
            }
            set('Q', LEASE_LABELS[row.lease_type])
        }
        row.graduations.slice(0, MAX_GRADUATIONS).forEach((step, stepIndex) => {
            setDate(18 + stepIndex * 2, step.date)
            set(
                19 + stepIndex * 2,
                absoluteRent(step.rent, row)
                    .divide(step.rent.payment_period === 'yearly' ? 12 : 1)
                    .toDecimal()
            )
        })
        row.rent_free_periods.slice(0, MAX_PERIODS).forEach((period, periodIndex) => {
            setDate(26 + periodIndex * 2, period.start_date)
            setDate(27 + periodIndex * 2, period.end_date)
        })
        datedVacancies(row).forEach((period, periodIndex) => {
            setDate(32 + periodIndex * 2, period.start_date)
            setDate(33 + periodIndex * 2, period.end_date)
        })
        set('AL', [row.note, ...tenantImportExportLimitations(row)].filter(Boolean).join('\n'))
        line.getCell('AL').alignment = { vertical: 'top', wrapText: true }
        setRent(['AM', 'AN', 'AO', 'AP'], row.market_rent)
    })
    return workbook.xlsx.writeBuffer()
}
